import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import './SettingsScreen.css';

function SettingsScreen() {
  const navigate = useNavigate();
  const { language, changeLanguage, t } = useTranslation();
  
  const [currentTheme, setCurrentTheme] = useState(
    localStorage.getItem('theme') || 'purple'
  );
  const [soundEffects, setSoundEffects] = useState(
    localStorage.getItem('soundEffects') !== 'false'
  );

  const getThemes = () => [
    { id: 'purple', name: t('themes.purple'), color: '#C8B6F6' },
    { id: 'pink', name: t('themes.pink'), color: '#FFB6D9' },
    { id: 'lightblue', name: t('themes.lightblue'), color: '#87CEEB' },
    { id: 'lightgreen', name: t('themes.lightgreen'), color: '#98D8AA' }
  ];

  const getLanguages = () => [
    { code: 'ko', name: '한국어', nativeName: '한국어', flag: '🇰🇷' },
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' }
  ];

  const handleThemeChange = (themeId) => {
    setCurrentTheme(themeId);
  };

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
  };

  const handleSoundEffectsChange = (enabled) => {
    setSoundEffects(enabled);
  };

  const handleApply = () => {
    localStorage.setItem('theme', currentTheme);
    localStorage.setItem('soundEffects', soundEffects);
    document.documentElement.setAttribute('data-theme', currentTheme);
    navigate('/');
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  return (
    <div className="settings-container">
      <header className="settings-header">
        <Link to="/" className="back-button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5m0 0l7 7m-7-7l7-7"></path>
          </svg>
        </Link>
        <h1 className="settings-title">{t('ui.settings')}</h1>
      </header>

      <main className="settings-main">
        <section className="settings-section">
          <h2 className="section-title">{t('ui.language')}</h2>
          <p className="section-description">
            {t('settings.language_desc')}
          </p>
          
          <div className="language-grid">
            {getLanguages().map((lang) => (
              <button
                key={lang.code}
                className={`language-option ${language === lang.code ? 'active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="language-flag">{lang.flag}</span>
                <span className="language-name">{lang.nativeName}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="settings-section">
          <h2 className="section-title">{t('ui.theme')}</h2>
          <p className="section-description">
            {t('settings.theme_desc')}
          </p>
          
          <div className="theme-grid">
            {getThemes().map((theme) => (
              <button
                key={theme.id}
                className={`theme-option ${currentTheme === theme.id ? 'active' : ''}`}
                onClick={() => handleThemeChange(theme.id)}
                style={{ '--theme-color': theme.color }}
              >
                <div className="theme-preview"></div>
                <span className="theme-name">{theme.name}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="settings-section">
          <h2 className="section-title">{t('settings.accessibility')}</h2>
          
          <div className="setting-item">
            <label className="setting-label">
              <input type="checkbox" className="setting-checkbox" />
              <span>{t('settings.large_text')}</span>
            </label>
            <p className="setting-description">{t('settings.large_text_desc')}</p>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              <input type="checkbox" className="setting-checkbox" />
              <span>{t('settings.voice_guide')}</span>
            </label>
            <p className="setting-description">{t('settings.voice_guide_desc')}</p>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              <input type="checkbox" className="setting-checkbox" />
              <span>{t('settings.keyboard_hint')}</span>
            </label>
            <p className="setting-description">{t('settings.keyboard_hint_desc')}</p>
          </div>
        </section>

        <section className="settings-section">
          <h2 className="section-title">{t('settings.practice')}</h2>
          
          <div className="setting-item">
            <label className="setting-label">
              <span>{t('settings.typing_speed')}</span>
              <select className="setting-select">
                <option value="slow">{t('settings.speed_slow')}</option>
                <option value="normal">{t('settings.speed_normal')}</option>
                <option value="fast">{t('settings.speed_fast')}</option>
              </select>
            </label>
            <p className="setting-description">{t('settings.typing_speed_desc')}</p>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              <input 
                type="checkbox" 
                className="setting-checkbox" 
                checked={soundEffects}
                onChange={(e) => handleSoundEffectsChange(e.target.checked)}
              />
              <span>{t('settings.sound_effects')}</span>
            </label>
            <p className="setting-description">{t('settings.sound_effects_desc')}</p>
          </div>
        </section>
      </main>

      <div className="settings-footer">
        <button className="apply-button" onClick={handleApply}>
          {t('ui.apply')}
        </button>
      </div>
    </div>
  );
}

export default SettingsScreen;